import { onMounted, onUnmounted, ref } from 'vue'

const DEBOUNCE_MS = 100
/** Minimum height sent to parent when embedded in iframe (matches Flutter / Zlavadna expectation). */
const IFRAME_MIN_HEIGHT_PX = 800
let rafId: number | null = null
let timeoutId: ReturnType<typeof setTimeout> | null = null

function isInIframe(): boolean {
  try {
    return typeof window !== 'undefined' && window.self !== window.top
  } catch {
    return false
  }
}

function setBodyAndDocumentHeight(heightPx: number): void {
  const px = `${heightPx}px`
  if (document.body?.style) {
    document.body.style.height = px
    document.body.style.minHeight = px
  }
  if (document.documentElement?.style) {
    document.documentElement.style.height = px
    document.documentElement.style.minHeight = px
  }
  const app = document.getElementById('app')
  if (app?.style) {
    app.style.height = px
    app.style.minHeight = px
  }
}

function postHeight(height: number): void {
  const effectiveHeight = isInIframe() ? Math.max(height, IFRAME_MIN_HEIGHT_PX) : height
  setBodyAndDocumentHeight(effectiveHeight)
  try {
    window.parent?.postMessage({ type: 'infinela:resize', height: effectiveHeight }, '*')
  } catch {
    // cross-origin or no parent
  }
}

export function useResizeNotifier(rootRef: { value: HTMLElement | null }) {
  const lastHeight = ref(0)

  function notify() {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      timeoutId = null
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = null
        const el = rootRef.value ?? document.documentElement
        const height = Math.max(
          el.scrollHeight ?? 0,
          el.offsetHeight ?? 0,
          document.documentElement.scrollHeight,
          document.body.scrollHeight
        )
        const raw = Math.ceil(height)
        const h = raw > 0 ? raw : (isInIframe() ? IFRAME_MIN_HEIGHT_PX : 0)
        if (h > 0 && h !== lastHeight.value) {
          lastHeight.value = h
          postHeight(h)
        }
      })
    }, DEBOUNCE_MS)
  }

  function notifyWithHeight(height: number) {
    const h = Math.ceil(height)
    if (h <= 0 && !isInIframe()) return
    const effective = h > 0 ? h : IFRAME_MIN_HEIGHT_PX
    lastHeight.value = effective
    postHeight(effective)
  }

  let observer: ResizeObserver | null = null

  onMounted(() => {
    observer = new ResizeObserver(notify)
    observer.observe(document.documentElement)
    observer.observe(document.body)
    if (rootRef.value) observer.observe(rootRef.value)
    notify()
    window.addEventListener('resize', notify)
  })

  onUnmounted(() => {
    observer?.disconnect()
    window.removeEventListener('resize', notify)
    if (timeoutId) clearTimeout(timeoutId)
    if (rafId !== null) cancelAnimationFrame(rafId)
  })

  return { notify, notifyWithHeight }
}
